import ApiService from './apiService';
import axios from 'axios';

/**
 * Service pour gérer les opérations liées aux factures proforma
 */
class ProformaInvoiceService {
    /**
     * Récupérer la liste des factures proforma avec filtres et pagination
     *
     * @param {Object} params Paramètres de filtrage et pagination :
     *   @param {string}   [params.search]            Recherche texte (numéro proforma ou nom client, max: 100 caractères)
     *   @param {number}   [params.per_page]          Nombre de résultats par page (min:1, max:100)
     *   @param {number}   [params.page]              Numéro de page (min:1)
     *   @param {string}   [params.status]            Statut de la proforma ('draft', 'accepted', 'rejected')
     *   @param {number}   [params.customer_id]        Filtrer par client (ID existant)
     *   @param {string}   [params.date_from]         Date de début (YYYY-MM-DD)
     *   @param {string}   [params.date_to]           Date de fin (YYYY-MM-DD, >= date_from)
     *   @param {Array}    [params.with]              Relations à charger (array, ex: ['customer','user','sale','items','items.product'])
     *   @param {string}   [params.sort_by]            Champ de tri (par défaut: 'created_at')
     *   @param {string}   [params.sort_order]        Ordre de tri ('asc' ou 'desc', par défaut: 'desc')
     * @returns {Promise} Réponse de l'API avec la liste des factures proforma
     */
    getProformaInvoices(params = {}) {
        return ApiService.get('/proforma-invoices', params);
    }

    /**
     * Récupérer une facture proforma par son ID
     * @param {Number} id ID de la facture proforma
     * @param {Object} params Paramètres additionnels (relations à charger)
     * @returns {Promise} Réponse de l'API avec les détails de la facture proforma
     */
    getProformaInvoice(id, params = {}) {
        return ApiService.get(`/proforma-invoices/${id}`, params);
    }

    /**
     * Créer une nouvelle facture proforma
     * @param {Object} proformaData Données de la facture proforma à créer
     * @param {Array} proformaData.items Articles de la proforma (requis)
     * @param {Object} proformaData.items[].product_id ID du produit (requis)
     * @param {number} proformaData.items[].quantity Quantité (requis, min: 0.01)
     * @param {number} [proformaData.items[].price_excl_tax] Prix HT (optionnel, utilise le prix du produit par défaut)
     * @param {string} [proformaData.items[].note] Note pour l'article (optionnel, max: 500)
     * @param {number} [proformaData.discount] Remise (optionnel, min: 0)
     * @param {number} [proformaData.customer_id] ID du client existant (optionnel)
     * @param {Object} [proformaData.customer] Données pour créer un nouveau client (optionnel)
     * @param {string} proformaData.customer.name Nom du client (requis si customer fourni)
     * @param {string} [proformaData.customer.email] Email du client
     * @param {string} [proformaData.customer.phone] Téléphone du client
     * @param {string} [proformaData.customer.address] Adresse du client
     * @param {string} [proformaData.customer.city] Ville du client
     * @param {string} [proformaData.customer.state] État/Région du client
     * @param {string} [proformaData.customer.zip] Code postal du client
     * @param {string} [proformaData.validity_date] Date de validité (YYYY-MM-DD, >= aujourd'hui)
     * @param {string} [proformaData.notes] Notes générales (optionnel, max: 2000)
     * @param {string} [proformaData.terms] Conditions générales (optionnel, max: 2000)
     * @returns {Promise} Réponse de l'API avec la facture proforma créée
     */
    createProformaInvoice(proformaData) {
        return ApiService.post('/proforma-invoices', proformaData);
    }

    /**
     * Mettre à jour une facture proforma
     * @param {Number} id ID de la facture proforma
     * @param {Object} proformaData Données à mettre à jour (même structure que createProformaInvoice)
     * @returns {Promise} Réponse de l'API avec la facture proforma mise à jour
     */
    updateProformaInvoice(id, proformaData) {
        return ApiService.put(`/proforma-invoices/${id}`, proformaData);
    }

    /**
     * Supprimer une facture proforma
     * @param {Number} id ID de la facture proforma
     * @returns {Promise} Réponse de l'API
     */
    deleteProformaInvoice(id) {
        return ApiService.delete(`/proforma-invoices/${id}`);
    }

    /**
     * Convertir une facture proforma en vente différée
     * @param {Number} id ID de la facture proforma
     * @param {Object} convertData Données de conversion
     * @param {number} convertData.cash_register_id ID de la caisse (requis)
     * @returns {Promise} Réponse de l'API avec la vente créée et la proforma mise à jour
     */
    convertToSale(id, convertData) {
        return ApiService.post(`/proforma-invoices/${id}/convert`, convertData);
    }

    /**
     * Rejeter une facture proforma
     * @param {Number} id ID de la facture proforma
     * @returns {Promise} Réponse de l'API avec la facture proforma mise à jour
     */
    rejectProformaInvoice(id) {
        return ApiService.post(`/proforma-invoices/${id}/reject`);
    }

    /**
     * Dupliquer une facture proforma
     * @param {Number} id ID de la facture proforma à dupliquer
     * @returns {Promise} Réponse de l'API avec la nouvelle facture proforma créée
     */
    duplicateProformaInvoice(id) {
        return ApiService.post(`/proforma-invoices/${id}/duplicate`);
    }

    /**
     * Télécharger le PDF d'une facture proforma
     * @param {Number|String} id ID de la facture proforma
     * @param {boolean} open Si true, ouvre le PDF dans un nouvel onglet au lieu de le télécharger
     * @returns {Promise} Télécharge ou ouvre le PDF
     */
    async downloadPdf(id, open = false) {
        if (!id) throw new Error('ID de facture proforma requis');

        // Utiliser axios directement pour avoir le contrôle sur responseType
        // Les intercepteurs d'axios sont déjà configurés par ApiService.init()
        const response = await axios.get(`/proforma-invoices/${id}/pdf`, {
            responseType: 'blob'
        });

        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);

        if (open) {
            // Ouvrir dans un nouvel onglet pour prévisualisation
            window.open(url, '_blank');
        } else {
            // Télécharger directement
            const link = document.createElement('a');
            link.href = url;
            link.download = `proforma-${id}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }

        // Nettoyer l'URL après un délai pour permettre l'ouverture
        setTimeout(() => window.URL.revokeObjectURL(url), 100);
    }

    /**
     * Obtenir les statistiques des factures proforma
     * @returns {Promise} Réponse de l'API avec les statistiques
     */
    getStats() {
        return ApiService.get('/proforma-invoices/stats');
    }

    /**
     * Rechercher des factures proforma
     * @param {String} query Terme de recherche (numéro proforma ou nom client)
     * @param {Object} options Options additionnelles (dates, statut, etc.)
     * @returns {Promise} Réponse de l'API avec les résultats de recherche
     */
    searchProformaInvoices(query, options = {}) {
        return ApiService.get('/proforma-invoices', {
            search: query,
            ...options
        });
    }

    /**
     * Obtenir les factures proforma par période
     * @param {String} dateFrom Date de début (format YYYY-MM-DD)
     * @param {String} dateTo Date de fin (format YYYY-MM-DD)
     * @param {Object} params Paramètres additionnels
     * @returns {Promise} Réponse de l'API avec les factures proforma de la période
     */
    getProformaInvoicesByPeriod(dateFrom, dateTo, params = {}) {
        return ApiService.get('/proforma-invoices', {
            date_from: dateFrom,
            date_to: dateTo,
            ...params
        });
    }

    /**
     * Obtenir les factures proforma par statut
     * @param {String} status Statut des factures proforma ('draft', 'accepted', 'rejected')
     * @param {Object} params Paramètres additionnels
     * @returns {Promise} Réponse de l'API avec les factures proforma filtrées par statut
     */
    getProformaInvoicesByStatus(status, params = {}) {
        return ApiService.get('/proforma-invoices', {
            status: status,
            ...params
        });
    }

    /**
     * Obtenir les factures proforma par client
     * @param {Number} customerId ID du client
     * @param {Object} params Paramètres additionnels
     * @returns {Promise} Réponse de l'API avec les factures proforma du client
     */
    getProformaInvoicesByCustomer(customerId, params = {}) {
        return ApiService.get('/proforma-invoices', {
            customer_id: customerId,
            ...params
        });
    }

    /**
     * Préparer les données d'une nouvelle facture proforma
     * @param {Array} items Articles de la proforma avec quantité
     * @param {number} [customerId] ID du client existant
     * @param {Object} [customer] Données pour créer un nouveau client
     * @param {number} [discount] Remise éventuelle
     * @param {string} [validityDate] Date de validité (YYYY-MM-DD)
     * @param {string} [notes] Notes générales
     * @param {string} [terms] Conditions générales
     * @returns {Object} Données formatées pour l'API
     */
    prepareProformaData(items, customerId = null, customer = null, discount = 0, validityDate = null, notes = null, terms = null) {
        const data = {
            items: items.map(item => ({
                product_id: item.product_id,
                quantity: item.quantity,
                price_excl_tax: item.price_excl_tax,
                note: item.note || null
            })),
            discount: discount || 0
        };

        if (customerId) {
            data.customer_id = customerId;
        } else if (customer) {
            data.customer = customer;
        }

        if (validityDate) {
            data.validity_date = validityDate;
        }

        if (notes) {
            data.notes = notes;
        }

        if (terms) {
            data.terms = terms;
        }

        return data;
    }

    /**
     * Formatter une facture proforma pour l'affichage
     * @param {Object} proforma Facture proforma
     * @returns {Object} Facture proforma formatée
     */
    formatProformaForDisplay(proforma) {
        if (!proforma) return null;

        return {
            ...proforma,
            formattedDate: this.formatDate(proforma.proforma_date),
            formattedValidityDate: proforma.validity_date ? this.formatDate(proforma.validity_date) : null,
            formattedTotal: this.formatCurrency(proforma.total_incl_tax),
            formattedStatus: this.getStatusLabel(proforma.status),
            canBeModified: this.canBeModified(proforma),
            canBeConverted: this.canBeConverted(proforma)
        };
    }

    /**
     * Formatter une date
     * @param {String} dateString Date au format ISO
     * @returns {String} Date formatée
     */
    formatDate(dateString) {
        if (!dateString) return '';

        const date = new Date(dateString);
        return date.toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    }

    /**
     * Formatter un montant en devise
     * @param {Number} amount Montant
     * @returns {String} Montant formaté
     */
    formatCurrency(amount) {
        if (amount === undefined || amount === null) return '';

        return new Intl.NumberFormat('fr-FR', {
            style: 'currency',
            currency: 'XOF',
            minimumFractionDigits: 0
        }).format(amount);
    }

    /**
     * Obtenir le libellé d'un statut
     * @param {String} status Code du statut
     * @returns {String} Libellé du statut
     */
    getStatusLabel(status) {
        const statusLabels = {
            'draft': 'Brouillon',
            'accepted': 'Acceptée',
            'rejected': 'Rejetée'
        };

        return statusLabels[status] || status;
    }

    /**
     * Vérifier si une facture proforma peut être modifiée
     * @param {Object} proforma Facture proforma
     * @returns {boolean} True si la proforma peut être modifiée
     */
    canBeModified(proforma) {
        // Une proforma ne peut pas être modifiée si elle est liée à une vente
        return !proforma.sale_id && proforma.status === 'draft';
    }

    /**
     * Vérifier si une facture proforma peut être convertie en vente
     * @param {Object} proforma Facture proforma
     * @returns {boolean} True si la proforma peut être convertie
     */
    canBeConverted(proforma) {
        // Une proforma peut être convertie si elle est en brouillon ou acceptée et non liée à une vente
        return (proforma.status === 'draft' || proforma.status === 'accepted') && !proforma.sale_id;
    }

    /**
     * Obtenir la couleur du badge de statut
     * @param {String} status Code du statut
     * @returns {String} Classe CSS pour la couleur
     */
    getStatusColor(status) {
        const statusColors = {
            'draft': 'bg-gray-100 text-gray-800',
            'accepted': 'bg-green-100 text-green-800',
            'rejected': 'bg-red-100 text-red-800'
        };

        return statusColors[status] || 'bg-gray-100 text-gray-800';
    }
}

export default new ProformaInvoiceService();

